// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { resolveFlowEngine } from "../../../lib/flow/utils.js"

// Toast live-region roles (FIX A): failure keys assert with role="alert"
// (aria-live assertive); every other toast is a polite status region.
const ALERT_TOAST_KEYS = ["saveFailed", "loadFailed", "noSaved"]

// Top toolbar bar — actions like save, export, undo, redo.
//
// Outlets (M4 fix): the toolbar resolves the engine through the outlet
// attribute the component templates emit (data-ui--flow-toolbar-bar-ui--flow-
// outlet); the legacy id lookup stays ONLY as a fallback for markup without a
// connected outlet, keyed by the id in the toolbar's own outlet attribute
// (resolveFlowEngine) so multi-canvas pages never drive the wrong graph.
export default class extends Controller {
  // `title` is the bar's visible title element (UX audit — the value used to
  // surface only as a data attribute); no rendering logic reads it.
  static targets = ["zoomLevel", "nodeCount", "edgeCount", "title"]
  static outlets = ["ui--flow"]

  static values = {
    // The component always emits a title (ToolbarBarComponent#resolved_title,
    // default "Flow"); this default only covers hand-authored markup.
    title: { type: String, default: "Flow" },
    // Toast copy is host-overridable (M7): point
    // data-ui--flow-toolbar-bar-toasts-value at localized strings, e.g.
    // {"saved":"Gespeichert"}. Keys are fixed; values are free text.
    toasts: {
      type: Object,
      default: {
        saved: "Flow saved",
        saveFailed: "Could not save — storage unavailable",
        loaded: "Flow loaded",
        noSaved: "No saved flow found",
        loadFailed: "Failed to load — corrupted data",
        cleared: "Canvas cleared",
        exported: "Exported flow.json"
      }
    }
  }

  connect() {
    this._onChange = () => this._updateStats()
    // Mutation-driven stats (perf): counts refresh on membership changes,
    // zoom on viewport changes. The old listener set included nodemove, so a
    // node drag re-ran two document-wide querySelectorAll scans per frame.
    this._events = ["viewportchange", "connect", "nodedelete", "edgedelete", "ready", "nodesadd", "edgesadd", "nodeselect", "restore", "undo", "redo"]
    this._events.forEach(e => document.addEventListener(`ui--flow:${e}`, this._onChange))

    // Multiple delayed updates to catch nodes after their controllers connect
    // — tracked so disconnect can cancel them (S9: they used to fire into a
    // torn-down controller).
    this._timers = [300, 800, 1500, 3000].map(ms => setTimeout(() => this._updateStats(), ms))
    this._toastTimers = new Set()
    this._toasts = new Set()
  }

  disconnect() {
    if (this._events) this._events.forEach(e => document.removeEventListener(`ui--flow:${e}`, this._onChange))
    if (this._timers) { this._timers.forEach(t => clearTimeout(t)); this._timers = [] }
    // Pending toast removals must not linger after teardown — cancel the
    // timers and drop the toast elements outright.
    if (this._toastTimers) { this._toastTimers.forEach(t => clearTimeout(t)); this._toastTimers.clear() }
    if (this._toasts) { this._toasts.forEach(el => el.remove()); this._toasts.clear() }
  }

  _getFlow() { return resolveFlowEngine(this) }

  // S9: the saved-graph key is scoped per canvas — the pre-conek global key
  // made every canvas on the page share (and overwrite) a single slot.
  _storageKey(flow) {
    return `conek-ui-flow-state:${flow?.element?.id || "default"}`
  }

  // Actions
  zoomIn()   { this._getFlow()?.zoomIn() }
  zoomOut()  { this._getFlow()?.zoomOut() }
  fitView()  { this._getFlow()?.fitView() }

  saveFlow() {
    const flow = this._getFlow()
    if (!flow) return
    const state = flow.toObject()
    try {
      localStorage.setItem(this._storageKey(flow), JSON.stringify(state))
      this._showToast("saved")
    } catch (e) {
      // Quota / privacy-mode denial must surface as copy, not an exception.
      this._showToast("saveFailed")
    }
  }

  loadFlow() {
    const flow = this._getFlow()
    if (!flow) return
    let saved = null
    try {
      saved = localStorage.getItem(this._storageKey(flow))
    } catch (e) {
      saved = null
    }
    if (!saved) { this._showToast("noSaved"); return }
    try {
      flow.fromObject(JSON.parse(saved))
      this._showToast("loaded")
    } catch (e) {
      this._showToast("loadFailed")
    }
  }

  clearFlow() {
    const flow = this._getFlow()
    if (!flow) return
    // S9: ONE undo entry for the whole clear — the old per-element
    // removeNode/removeEdge loop pushed N entries (a 100-element clear
    // exhausted the history and undid one element per step). fromObject
    // routes through the engine's snapshot path: one _pushUndo, per-element
    // delete events still fire, and a `restore` event closes it out.
    flow.fromObject({ nodes: [], edges: [] })
    this._showToast("cleared")
  }

  exportJSON() {
    const flow = this._getFlow()
    if (!flow) return
    const state = flow.toObject()
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = "flow.json"; a.click()
    URL.revokeObjectURL(url)
    this._showToast("exported")
  }

  undo() { this._getFlow()?.undo() }

  redo() { this._getFlow()?.redo() }

  _updateStats() {
    const flow = this._getFlow()
    if (this.hasZoomLevelTarget && flow) {
      this.zoomLevelTarget.textContent = `${flow.zoomPercentage}%`
    }
    // Counts come from the engine's maps (O(1)) — no document-wide scans; a
    // DOM fallback below covers hosts whose flow controller is gated behind
    // the legacy id lookup and couldn't be resolved.
    if (this.hasNodeCountTarget) {
      this.nodeCountTarget.textContent = flow
        ? `${flow._nodes.size} nodes`
        : `${this.element.closest("body")?.querySelectorAll("[data-ui--flow-node]").length ?? 0} nodes`
    }
    if (this.hasEdgeCountTarget) {
      this.edgeCountTarget.textContent = flow
        ? `${flow._edges.size} edges`
        : `${this.element.closest("body")?.querySelectorAll("path.ui-flow-edge").length ?? 0} edges`
    }
  }

    // Failure keys announce with role="alert"; everything else is a polite
  // status. Without the live-region a screen reader heard nothing — the
  // naked appended div announces nothing.
  _showToast(key) {
    const msg = this.toastsValue[key] || key
    const toast = document.createElement("div")
    toast.className = "ui-flow-toast"
    if (ALERT_TOAST_KEYS.includes(key)) {
      toast.setAttribute("role", "alert")
      toast.setAttribute("aria-live", "assertive")
    } else {
      toast.setAttribute("role", "status")
      toast.setAttribute("aria-live", "polite")
    }
    toast.textContent = msg
    document.body.appendChild(toast)
    this._toasts?.add(toast)
    const timer = setTimeout(() => {
      this._toastTimers?.delete(timer)
      this._toasts?.delete(toast)
      toast.remove()
    }, 2000)
    this._toastTimers?.add(timer)
  }
}
